/**
 * Internal dependencies.
 */
import { Checkout, LineItem, Product, Upsell } from 'src/types';
import { createIdGuard } from '@store/utils';
import { state } from './store';

/**
 * Tracked upsell line item ids are persisted so a reload or back-navigation
 * replay of an already-accepted upsell does not re-fire purchase events.
 * A genuine re-purchase returns a new line item id, so it still tracks.
 */
const trackedGuard = createIdGuard('scTrackedUpsells');

export type UpsellAcceptedDetail = {
  checkout: Checkout;
  line_item: LineItem;
  upsell: Upsell;
  product: Product;
};

// Lets addEventListener('scUpsellAccepted', ...) infer the typed event.
declare global {
  interface WindowEventMap {
    scUpsellAccepted: CustomEvent<UpsellAcceptedDetail>;
  }
}

/**
 * Upsell accepted event.
 *
 * The upsell charge happens outside the checkout store, so the checkout
 * completion events never fire for it — this is the only client-side
 * signal that an upsell was purchased.
 */
export const dispatchUpsellAccepted = (checkout: Checkout, lineItem: LineItem): void => {
  if (!lineItem?.id) return;
  if (trackedGuard.has(lineItem.id)) return; // only once per upsell line item.
  trackedGuard.add(lineItem.id);

  const event = new CustomEvent<UpsellAcceptedDetail>('scUpsellAccepted', {
    detail: {
      checkout,
      line_item: lineItem,
      upsell: state.upsell,
      product: state.product,
    },
    bubbles: true,
  });
  document.dispatchEvent(event);
};
